import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpDown, Camera, ChevronDown, ChevronUp, GripVertical, ImagePlus, Info, Loader2, Plus, ScanBarcode, Sparkles, X,
} from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button'; 
import AuthMessage from '../../components/AuthMessage';
import { useAuth } from '../../context/AuthContext';
import { useProducts } from '../../context/ProductsContext';
import { lookupBarcodeEnrich, listLots } from '../../api/inventory';
import { SELLING_PRICE_MRP_MESSAGE, isSellingBelowMrp } from '../../utils/posMoney';
import ShelfLotsEditor from './ShelfLotsEditor';
import { GST_RATES } from '../../utils/gst';
import { tombstoneTarget } from '../../utils/productMerge';
import { canAutoSku, generateProductSku } from '../../utils/sku';
import form from '../../styles/form.module.css';
import styles from './ProductFormDrawer.module.css';

const UNITS = ['ea', 'kg', 'bag', 'pack', 'bottle', 'pouch', 'tray', 'piece'];
const NET_QTY_UNITS = ['kg', 'g', 'l', 'ml', 'pcs'];
const SOLD_BY_OPTIONS = [
  { value: 'EACH', label: 'Pack' },
  { value: 'WEIGHT', label: 'Loose / weigh' },
];
// 1 g step and min so POS can sell 130 g, 250 g, 2 kg, etc. without a 50 g / 250 g floor.
const LOOSE_QTY_STEP = '0.001';
const LOOSE_MIN_ORDER_QTY = '0.001';
const BARCODE_TYPES = ['EAN13', 'EAN8', 'UPC', 'CODE128', 'QR'];
const DIMENSION_UNITS = ['cm', 'in', 'mm'];
const ITEM_TYPES = [
  { value: 'general', label: 'General merchandise' },
  { value: 'food', label: 'Food & grocery' },
];
const MAX_IMAGES = 6;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const emptyForm = {
  name: '', sku: '', barcode: '', barcodeType: BARCODE_TYPES[0], category: '',
  brand: '', description: '', itemType: ITEM_TYPES[0].value,
  costPrice: '', price: '', mrp: '',
  soldBy: 'EACH',
  unit: UNITS[0], stock: '', lowStockThreshold: '10', minOrderQty: '1', maxOrderQty: '10', qtyStep: '1',
  netQty: '', netQtyUnit: NET_QTY_UNITS[0], packCount: '1',
  gstRate: GST_RATES[0], hsnCode: '',
  dimLength: '', dimWidth: '', dimHeight: '', dimUnit: DIMENSION_UNITS[0],
  nutritionServingSize: '', nutritionEnergyKcal: '', nutritionProteinG: '', nutritionCarbsG: '', nutritionFatG: '',
  images: [],
};

const hasAny = (values) => values.some((v) => v !== '' && v !== undefined && v !== null);

const uid = () => Math.random().toString(36).slice(2);

const readAsDataUrl = (file, cb) => {
  const reader = new FileReader();
  reader.onload = () => cb(reader.result);
  reader.readAsDataURL(file);
};

function matchCategoryId(value, activeCategories, fallbackId) {
  const needle = (value || '').trim().toLowerCase();
  if (!needle) return fallbackId;
  const byId = activeCategories.find((c) => c.id.toLowerCase() === needle);
  if (byId) return byId.id;
  const byName = activeCategories.find((c) => c.name.toLowerCase() === needle);
  return byName?.id || fallbackId;
}

function formatEnrichSource(source) {
  if (!source || source === 'catalog') return 'catalog';
  return String(source).replace(/_/g, ' ');
}

function imageLabel(img, index) {
  if (img?.file?.name) return img.file.name;
  if (img?.url) {
    try {
      const path = decodeURIComponent(new URL(img.url).pathname.split('/').pop() || '');
      if (path) return path;
    } catch {
      // fall through
    }
  }
  return `Image ${index + 1}`;
}

function ProductCameraModal({ onCapture, onClose, onUseFilePicker }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [capturing, setCapturing] = useState(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Camera is not supported in this browser.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setReady(true);
      } catch {
        if (!cancelled) {
          setError('Could not open the camera. Check browser permissions, or use the file picker.');
        }
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [stopCamera]);

  const handleCapture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !ready || capturing) return;
    setCapturing(true);
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      setCapturing(false);
      if (!blob) return;
      stopCamera();
      const file = new File([blob], `product-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
    }, 'image/jpeg', 0.9);
  };

  return createPortal(
    <div className={styles.cameraOverlay} onClick={onClose} role="presentation">
      <div className={styles.cameraCard} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Take photo">
        <div className={styles.cameraHeader}>
          <h3 className={styles.cameraTitle}>Take photo</h3>
          <button type="button" className={styles.modalCloseBtn} onClick={onClose} aria-label="Close camera">
            <X size={18} />
          </button>
        </div>
        <div className={styles.cameraBody}>
          {error ? (
            <div className={styles.cameraError}>
              <p>{error}</p>
              <Button variant="outline" onClick={onUseFilePicker}>Open device camera / files</Button>
            </div>
          ) : (
            <>
              <div className={styles.cameraViewport}>
                <video ref={videoRef} className={styles.cameraVideo} playsInline muted autoPlay />
                {!ready && <p className={styles.cameraStatus}>Starting camera…</p>}
              </div>
              <canvas ref={canvasRef} className={styles.cameraCanvas} />
            </>
          )}
        </div>
        <div className={styles.cameraFooter}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {!error && (
            <Button icon={Camera} onClick={handleCapture} disabled={!ready || capturing}>
              {capturing ? 'Capturing…' : 'Capture'}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ProductImageOrderModal({ images, onSave, onClose, disabled }) {
  const [ordered, setOrdered] = useState(images);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    setOrdered(images);
  }, [images]);

  const moveImage = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= ordered.length || fromIdx === toIdx) return;
    setOrdered((prev) => {
      const next = [...prev];
      const [item] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, item);
      return next;
    });
  };

  const handleDragStart = (idx) => {
    if (disabled) return;
    setDragIdx(idx);
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (disabled || dragIdx === null || dragIdx === idx) return;
    moveImage(dragIdx, idx);
    setDragIdx(idx);
  };

  const orderChanged = ordered.some((img, idx) => img !== images[idx]);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Image order</h3>
          <button type="button" className={styles.modalCloseBtn} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.imageOrderHint}>
            Drag images or use arrows. The first image is the cover photo.
          </p>
          <ol className={styles.imageOrderList}>
            {ordered.map((img, idx) => (
              <li
                key={img.id || img.url || idx}
                className={`${styles.imageOrderItem} ${dragIdx === idx ? styles.imageOrderItemDragging : ''}`}
                draggable={!disabled}
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={() => setDragIdx(null)}
              >
                <span className={styles.imageOrderGrip} aria-hidden="true">
                  <GripVertical size={14} />
                </span>
                <span className={styles.imageOrderRank}>{idx + 1}</span>
                {img.url ? (
                  <img src={img.url} alt="" className={styles.sortThumb} />
                ) : (
                  <div className={styles.sortThumbPlaceholder} />
                )}
                <span className={styles.imageOrderName} title={imageLabel(img, idx)}>
                  {imageLabel(img, idx)}
                </span>
                {idx === 0 && <span className={styles.imageOrderCoverBadge}>Cover</span>}
                <div className={styles.imageOrderActions}>
                  <button
                    type="button"
                    className={styles.imageOrderMoveBtn}
                    onClick={() => moveImage(idx, idx - 1)}
                    disabled={disabled || idx === 0}
                    aria-label={`Move image ${idx + 1} up`}
                  >
                    <ChevronUp size={14} />
                  </button>
                  <button
                    type="button"
                    className={styles.imageOrderMoveBtn}
                    onClick={() => moveImage(idx, idx + 1)}
                    disabled={disabled || idx === ordered.length - 1}
                    aria-label={`Move image ${idx + 1} down`}
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className={styles.modalFooter}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(ordered)} disabled={disabled || !orderChanged}>
            Save order
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ProductFormDrawer({ open, onClose, onSave, product, onOpenExisting }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories, products } = useProducts();
  const activeCategories = categories.filter((c) => c.status === 'ACTIVE');
  const empty = {
    ...emptyForm,
    category: activeCategories[0]?.id || '',
    gstRate: activeCategories[0]?.defaultGstRate ?? emptyForm.gstRate,
  };
  const [form_, setForm] = useState(empty);
  const [skuAuto, setSkuAuto] = useState(false);
  const [autoGenerateSku, setAutoGenerateSku] = useState(true);
  const isEdit = !!product;
  const coverInputRef = useRef(null);
  const addInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [imageOrderOpen, setImageOrderOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichNotice, setEnrichNotice] = useState(null);
  const [lots, setLots] = useState([]);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lotsError, setLotsError] = useState('');
  const [lotsSupported, setLotsSupported] = useState(true);

  const barcodeInputRef = useRef(null);
  const lastLookupRef = useRef('');

  useEffect(() => {
    if (!product) {
      setForm(empty);
      setSkuAuto(false);
      setAutoGenerateSku(true);
    } else {
      const next = { ...empty, ...product };
      next.soldBy = product.soldBy === 'WEIGHT' ? 'WEIGHT' : 'EACH';
      const nutritionMarksFood = hasAny([
        next.nutritionServingSize, next.nutritionEnergyKcal, next.nutritionProteinG, next.nutritionCarbsG, next.nutritionFatG,
      ]);
      next.itemType = product.itemType === 'food' || product.itemType === 'general'
        ? product.itemType
        : (nutritionMarksFood ? 'food' : 'general');
      if (!GST_RATES.includes(Number(next.gstRate))) {
        next.gstRate = GST_RATES[0];
      }
      setForm(next);
      setSkuAuto(false);
      setAutoGenerateSku(false);
    }
    setImagesToDelete([]);
    setImageOrderOpen(false);
    setCameraOpen(false);
    setError('');
    setSubmitting(false);
    setEnrichNotice(null);
    lastLookupRef.current = '';
    setLots([]);
    setLotsError('');
    setLotsSupported(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, open]);

  useEffect(() => {
    if (!open || !isEdit || !product?.id || !user?.idToken) return undefined;
    let cancelled = false;
    (async () => {
      setLotsLoading(true);
      setLotsError('');
      try {
        const { lots: openLots } = await listLots(product.id, user.idToken);
        if (cancelled) return;
        setLotsSupported(true);
        setLots(openLots);
      } catch (err) {
        if (cancelled) return;
        if (err.status === 404 || err.message === 'not_found') {
          setLotsSupported(false);
          setLots([]);
        } else {
          setLotsError(err.message || 'Could not load lots.');
        }
      } finally {
        if (!cancelled) setLotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isEdit, product?.id, user?.idToken]);

  useEffect(() => {
    if (!open || isEdit) return undefined;
    const timer = setTimeout(() => barcodeInputRef.current?.focus({ preventScroll: true }), 120);
    return () => clearTimeout(timer);
  }, [open, isEdit]);

  // Auto-fill SKU on create when name, category, and barcode are set.
  useEffect(() => {
    if (!open || isEdit || !autoGenerateSku) return;
    if (!canAutoSku(form_)) {
      if (skuAuto && form_.sku) {
        setForm((f) => ({ ...f, sku: '' }));
        setSkuAuto(false);
      }
      return;
    }
    if (!skuAuto && String(form_.sku || '').trim()) return;
    const nextSku = generateProductSku(
      { name: form_.name, brand: form_.brand, category: form_.category, barcode: form_.barcode },
      products,
      activeCategories,
    );
    if (!nextSku || nextSku === form_.sku) return;
    setForm((f) => ({ ...f, sku: nextSku }));
    setSkuAuto(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to fields that drive SKU
  }, [open, isEdit, autoGenerateSku, form_.name, form_.brand, form_.category, form_.barcode, skuAuto]);

  const set = (key) => (e) => {
    const value = e.target.value;
    if (key === 'sku') {
      setSkuAuto(false);
      setForm((f) => ({ ...f, sku: value }));
      return;
    }
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleCoverFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/') || file.size > MAX_IMAGE_BYTES) return;
    const prevCover = form_.images[0];
    if (prevCover && !prevCover.file) setImagesToDelete((prev) => [...prev, prevCover]);
    readAsDataUrl(file, (url) => {
      setForm((f) => ({ ...f, images: [{ id: uid(), url, file }, ...f.images.slice(1)] }));
    });
  };

  const handleAddFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const room = MAX_IMAGES - form_.images.length;
    const accepted = files
      .filter((file) => file.type.startsWith('image/') && file.size <= MAX_IMAGE_BYTES)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
      .slice(0, room);
    accepted.forEach((file) => {
      readAsDataUrl(file, (url) => {
        setForm((f) => ({ ...f, images: [...f.images, { id: uid(), url, file }] }));
      });
    });
  };

  const addCameraImage = (file) => {
    if (!file || !file.type.startsWith('image/') || file.size > MAX_IMAGE_BYTES) return;
    readAsDataUrl(file, (url) => {
      setForm((f) => {
        if (f.images.length >= MAX_IMAGES) return f;
        if (f.images.length === 0) {
          return { ...f, images: [{ id: uid(), url, file }] };
        }
        return { ...f, images: [...f.images, { id: uid(), url, file }] };
      });
    });
  };

  /** Native file input with capture — fallback when live camera is unavailable. */
  const handleCameraFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    addCameraImage(file);
  };

  const openCamera = () => {
    if (submitting || form_.images.length >= MAX_IMAGES) return;
    // Prefer in-app camera (works on mobile + desktop HTTPS). Fall back to capture input.
    if (navigator.mediaDevices?.getUserMedia) {
      setCameraOpen(true);
      return;
    }
    cameraInputRef.current?.click();
  };

  const openNativeCameraPicker = () => {
    setCameraOpen(false);
    // Allow modal unmount before triggering the native picker.
    window.setTimeout(() => cameraInputRef.current?.click(), 50);
  };

  const removeImage = (id) => {
    const img = form_.images.find((i) => i.id === id);
    if (img && !img.file) setImagesToDelete((prev) => [...prev, img]);
    setForm((f) => ({ ...f, images: f.images.filter((i) => i.id !== id) }));
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    if (isEdit) {
      setForm((f) => ({ ...f, category: categoryId }));
      return;
    }
    const cat = activeCategories.find((c) => c.id === categoryId);
    setForm((f) => ({ ...f, category: categoryId, gstRate: cat?.defaultGstRate ?? f.gstRate }));
  };

  const applySuggestion = (suggestion, barcode) => {
    const categoryId = matchCategoryId(suggestion?.category, activeCategories, form_.category || empty.category);
    const cat = activeCategories.find((c) => c.id === categoryId);
    const netQtyUnit = NET_QTY_UNITS.includes(suggestion?.net_qty_unit)
      ? suggestion.net_qty_unit
      : form_.netQtyUnit;
    const foodUnits = ['g', 'kg', 'ml', 'l'];
    setForm((f) => ({
      ...f,
      barcode,
      name: suggestion?.name || f.name,
      brand: suggestion?.brand || f.brand,
      description: isEdit ? f.description : (suggestion?.description || f.description),
      category: categoryId,
      gstRate: cat?.defaultGstRate ?? f.gstRate,
      netQty: isEdit ? f.netQty : (suggestion?.net_qty ?? f.netQty),
      netQtyUnit,
      itemType: foodUnits.includes(netQtyUnit) ? 'food' : f.itemType,
      images: isEdit
        ? f.images
        : (suggestion?.image_url
          ? [{ id: uid(), url: suggestion.image_url, remoteImport: true }]
          : f.images),
    }));
  };

  const runBarcodeLookup = async (rawBarcode) => {
    const barcode = String(rawBarcode || '').trim();
    if (!barcode || !user?.idToken) return;
    if (barcode === lastLookupRef.current) return;
    if (!/^\d+$/.test(barcode)) {
      setEnrichNotice({ tone: 'warning', text: 'Barcode must be digits only (EAN-13 / UPC).' });
      return;
    }

    setEnriching(true);
    setEnrichNotice(null);
    lastLookupRef.current = barcode;

    try {
      const data = await lookupBarcodeEnrich(barcode, user.idToken);

      if (data.already_onboarded && data.item_id) {
        if (isEdit && product?.id === data.item_id) {
          if (data.suggestion) applySuggestion(data.suggestion, barcode);
          setEnrichNotice({ tone: 'info', text: 'This product is already in your catalog.' });
          return;
        }
        onOpenExisting?.(data.item_id);
        setEnrichNotice({
          tone: 'warning',
          text: `Already onboarded — opened existing product (${data.suggestion?.name || barcode}).`,
        });
        return;
      }

      if (data.found && data.suggestion) {
        applySuggestion(data.suggestion, barcode);
        setEnrichNotice({
          tone: 'success',
          text: `Prefilled from ${formatEnrichSource(data.source)}. Enter HSN, GST, and prices before saving.`,
        });
        return;
      }

      setForm((f) => ({ ...f, barcode }));
      setEnrichNotice({
        tone: 'info',
        text: 'Not found in external databases — enter product details manually.',
      });
    } catch (err) {
      if (err.message === 'forbidden') {
        lastLookupRef.current = '';
        setEnrichNotice(null);
        return;
      }
      if (err.message === 'invalid_barcode' || err.message === 'barcode_required') {
        setEnrichNotice({ tone: 'warning', text: 'Invalid barcode — use digits only.' });
        return;
      }
      if (err.name === 'AbortError' || err.message === 'timeout') {
        setForm((f) => ({ ...f, barcode }));
        setEnrichNotice({
          tone: 'info',
          text: 'Lookup timed out — enter product details manually.',
        });
        return;
      }
      setForm((f) => ({ ...f, barcode }));
      setEnrichNotice({
        tone: 'info',
        text: 'Could not enrich barcode — enter product details manually.',
      });
    } finally {
      setEnriching(false);
    }
  };

  const handleBarcodeChange = (e) => {
    const next = e.target.value;
    setForm((f) => ({ ...f, barcode: next }));
    if (next.trim() !== lastLookupRef.current) {
      setEnrichNotice(null);
    }
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    runBarcodeLookup(form_.barcode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const sell = Number(form_.price);
    const maxRetail = Number(form_.mrp);
    if (!isSellingBelowMrp(sell, maxRetail)) {
      setError(SELLING_PRICE_MRP_MESSAGE);
      return;
    }
    setSubmitting(true);
    try {
      // Only send image_url for a NEW remote cover (barcode enrich). Re-sending an
      // existing catalog/S3 URL on every edit can clear persisted images on the backend.
      const existingUrls = new Set((product?.images || []).map((img) => img.url).filter(Boolean));
      const remoteCover = form_.images.find((img) => img.url && !img.file);
      let imageUrl;
      if (remoteCover) {
        if (!isEdit) {
          imageUrl = remoteCover.url;
        } else if (remoteCover.remoteImport || !existingUrls.has(remoteCover.url)) {
          imageUrl = remoteCover.url;
        }
      }

      const body = {
        ...form_,
        price: sell,
        mrp: maxRetail,
        lowStockThreshold: Number(form_.lowStockThreshold) || 0,
        gstRate: Number(form_.gstRate) || 0,
        imageUrl,
        imagesToDelete,
      };
      // Opening stock is stock_qty on POST. PATCH does not change quantity on hand.
      // Description, low-stock alert, and net quantity stay as saved on edit.
      // Images follow the form (add / remove / multi-upload) on both create and edit.
      if (isEdit) {
        delete body.stock;
        body.description = product?.description || '';
        body.lowStockThreshold = product?.lowStockThreshold ?? '';
        body.netQty = product?.netQty ?? '';
        if (!imageUrl) delete body.imageUrl;
      } else {
        body.stock = Number(form_.stock) || 0;
      }
      await onSave(body);
      onClose();
    } catch (err) {
      const into = tombstoneTarget(err);
      if (into) {
        onClose();
        onOpenExisting?.(into);
        return;
      }
      setError(err.message === 'invalid_sold_by'
        ? 'Sold as must be Pack or Loose.'
        : (err.message || 'Could not save this product. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit product' : 'Add product'}
      subtitle={isEdit ? form_.sku : 'Create a new catalog listing'}
      width={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add product'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <AuthMessage>{error}</AuthMessage>

        {enrichNotice && (
          <div className={`${styles.enrichBanner} ${styles[`enrich${enrichNotice.tone.charAt(0).toUpperCase()}${enrichNotice.tone.slice(1)}`]}`}>
            {enrichNotice.text}
          </div>
        )}

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>
              <ScanBarcode size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
              Barcode
            </span>
            <div className={styles.barcodeFieldWrap}>
              <input
                ref={barcodeInputRef}
                className={`${form.input} ${styles.barcodeInput}`}
                value={form_.barcode}
                onChange={handleBarcodeChange}
                onKeyDown={handleBarcodeKeyDown}
                placeholder="Scan or type EAN, then Enter"
                inputMode="numeric"
                autoComplete="off"
                disabled={enriching}
              />
              {enriching && <Loader2 size={16} className={styles.barcodeSpinner} aria-hidden />}
            </div>
            <span className={form.hint}>Scan with HT15 or press Enter after digits to look up product details.</span>
          </label>
          <label className={form.field}>
            <span className={form.label}>Barcode type</span>
            <select className={form.select} value={form_.barcodeType} onChange={set('barcodeType')}>
              {BARCODE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        </div>

        <p className={form.label} style={{ marginBottom: 8 }}>Product images</p>
        <div className={styles.dropzoneRow}>
          <div
            className={styles.dropzoneTile}
            onClick={() => coverInputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            {form_.images[0] ? (
              <>
                <img src={form_.images[0].url} alt="Cover" className={styles.dropzoneImg} />
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={(e) => { e.stopPropagation(); removeImage(form_.images[0].id); }}
                  aria-label="Remove cover image"
                >
                  <X size={12} />
                </button>
              </>
            ) : (
              <>
                <ImagePlus size={18} />
                <span>Cover</span>
              </>
            )}
          </div>
          <input ref={coverInputRef} type="file" accept="image/*" hidden onChange={handleCoverFile} />

          {form_.images.slice(1).map((img) => (
            <div key={img.id} className={styles.dropzoneTile}>
              <img src={img.url} alt="" className={styles.dropzoneImg} />
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeImage(img.id)}
                aria-label="Remove image"
              >
                <X size={12} />
              </button>
            </div>
          ))}

          {form_.images.length < MAX_IMAGES && (
            <div
              className={styles.dropzoneTile}
              onClick={() => addInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label="Add images"
            >
              <Plus size={16} />
            </div>
          )}
          <input ref={addInputRef} type="file" accept="image/*" multiple hidden onChange={handleAddFiles} />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className={styles.visuallyHiddenInput}
            onChange={handleCameraFile}
            tabIndex={-1}
          />
        </div>
        <div className={styles.imageActionsRow}>
          <p className={styles.imageHint}>Up to {MAX_IMAGES} images. First tile is the cover.</p>
          <div className={styles.imageActionBtns}>
            <button
              type="button"
              className={styles.cameraBtn}
              onClick={openCamera}
              disabled={submitting || form_.images.length >= MAX_IMAGES}
              title="Take a photo with the camera"
            >
              <Camera size={13} />
              Take photo
            </button>
            {form_.images.length > 1 && (
              <button
                type="button"
                className={styles.reorderImagesBtn}
                onClick={() => setImageOrderOpen(true)}
                disabled={submitting}
              >
                <ArrowUpDown size={13} />
                Reorder images
              </button>
            )}
          </div>
        </div>

        {cameraOpen && (
          <ProductCameraModal
            onClose={() => setCameraOpen(false)}
            onUseFilePicker={openNativeCameraPicker}
            onCapture={(file) => {
              setCameraOpen(false);
              addCameraImage(file);
            }}
          />
        )}

        {imageOrderOpen && (
          <ProductImageOrderModal
            images={form_.images}
            disabled={submitting}
            onClose={() => setImageOrderOpen(false)}
            onSave={(ordered) => {
              setForm((f) => ({ ...f, images: ordered }));
              setImageOrderOpen(false);
            }}
          />
        )}

        <label className={form.field}>
          <span className={form.label}>Product name</span>
          <input className={form.input} required value={form_.name} onChange={set('name')} placeholder="e.g. Aashirvaad Atta 5kg" />
        </label>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>SKU</span>
            <input
              className={form.input}
              required
              value={form_.sku}
              onChange={set('sku')}
              placeholder={autoGenerateSku && !isEdit ? 'Auto from name, category & barcode' : 'AASH-ATTA-5KG'}
            />
            {!isEdit && (
              <span className={styles.skuAutoRow}>
                <Sparkles size={12} />
                <span>Auto-generate</span>
                <button
                  type="button"
                  className={`${form.toggle} ${autoGenerateSku ? form.toggleOn : ''}`}
                  onClick={() => {
                    if (autoGenerateSku) {
                      setAutoGenerateSku(false);
                      return;
                    }
                    setAutoGenerateSku(true);
                    // Re-enable auto updates for empty or previously auto SKUs
                    if (!String(form_.sku || '').trim() || skuAuto) setSkuAuto(true);
                  }}
                  aria-pressed={autoGenerateSku}
                  aria-label={autoGenerateSku ? 'Turn off auto-generate SKU' : 'Turn on auto-generate SKU'}
                  title={autoGenerateSku ? 'Auto-generate on' : 'Auto-generate off'}
                />
              </span>
            )}
            {!isEdit && autoGenerateSku && (
              <span className={form.hint}>
                Fills after name, category, and barcode are set. Edit the SKU to lock a manual value.
              </span>
            )}
          </label>
          <label className={form.field}>
            <span className={form.label}>Brand</span>
            <input className={form.input} value={form_.brand} onChange={set('brand')} placeholder="e.g. Aashirvaad" />
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Category</span>
            <select className={form.select} value={form_.category} onChange={handleCategoryChange}>
              {[...activeCategories]
                .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'en', { sensitivity: 'base' }))
                .map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
            </select>
          </label>
          <label className={form.field}>
            <span className={form.label}>Product type</span>
            <select className={form.select} value={form_.itemType} onChange={set('itemType')}>
              {ITEM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>GST rate</span>
            <select className={form.select} value={form_.gstRate} onChange={set('gstRate')}>
              {GST_RATES.map((r) => <option key={r} value={r}>{r}% GST</option>)}
            </select>
          </label>
          <label className={form.field}>
            <span className={form.label}>HSN code</span>
            <input className={form.input} required value={form_.hsnCode} onChange={set('hsnCode')} placeholder="e.g. 1006" />
          </label>
        </div>

        <label className={form.field}>
          <span className={form.label}>Description</span>
          <textarea
            className={form.textarea}
            value={form_.description}
            onChange={set('description')}
            placeholder="Whole wheat atta, stone-ground."
            disabled={isEdit}
          />
        </label>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Cost price</span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input className={form.input} type="number" min="0" value={form_.costPrice} onChange={set('costPrice')} />
            </div>
          </label>
          <label className={form.field}>
            <span className={form.label}>
              {isEdit
                ? 'Default / online catalog price'
                : form_.soldBy === 'WEIGHT'
                  ? `Rate per ${form_.unit || 'kg'}`
                  : 'Selling price'}
            </span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input className={form.input} type="number" required min="0" value={form_.price} onChange={set('price')} />
            </div>
            {isEdit && (
              <span className={form.hint}>
                Online price only. Save changes writes this. POS uses the shelf prices below.
              </span>
            )}
            {form_.soldBy === 'WEIGHT' && (
              <span className={form.hint}>POS multiplies this rate by the weighed kg.</span>
            )}
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Sold as</span>
            <select
              className={form.select}
              value={form_.soldBy}
              onChange={(e) => {
                const nextSoldBy = e.target.value;
                setForm((f) => ({
                  ...f,
                  soldBy: nextSoldBy,
                  unit: nextSoldBy === 'WEIGHT' && (f.unit === 'ea' || f.unit === 'piece') ? 'kg' : f.unit,
                  qtyStep: nextSoldBy === 'WEIGHT' ? LOOSE_QTY_STEP : '1',
                  minOrderQty: nextSoldBy === 'WEIGHT' ? LOOSE_MIN_ORDER_QTY : '1',
                }));
              }}
            >
              {SOLD_BY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span className={form.hint}>A 5 kg rice bag is Pack, not Loose. Do not flip a bag SKU to Loose.</span>
          </label>
          <label className={form.field}>
            <span className={form.label}>Selling unit</span>
            <select className={form.select} value={form_.unit} onChange={set('unit')}>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>MRP</span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input className={form.input} type="number" required min="0" value={form_.mrp} onChange={set('mrp')} />
            </div>
            <span className={form.hint}>Must be greater than or equal to the selling price.</span>
          </label>
          {isEdit ? (
            <label className={form.field}>
              <span className={form.label}>Status</span>
              <select className={form.select} value={form_.status || 'ACTIVE'} onChange={set('status')}>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DRAFT">Draft</option>
              </select>
            </label>
          ) : (
            <div className={form.field} aria-hidden="true" />
          )}
        </div>

        {isEdit && lotsSupported && (
          <ShelfLotsEditor
            key={product?.id}
            lots={lots}
            loading={lotsLoading}
            loadError={lotsError}
            soldBy={form_.soldBy}
            unit={form_.unit}
            catalogPrice={form_.price}
            productId={product?.id}
            token={user?.idToken}
            onLotsChange={setLots}
          />
        )}

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Net quantity</span>
            <input
              className={form.input}
              type="number"
              min="0"
              value={form_.netQty}
              onChange={set('netQty')}
              placeholder="5"
              disabled={isEdit}
            />
          </label>
          <label className={form.field}>
            <span className={form.label}>Net qty unit</span>
            <select className={form.select} value={form_.netQtyUnit} onChange={set('netQtyUnit')}>
              {NET_QTY_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Pack count</span>
            <input className={form.input} type="number" min="1" value={form_.packCount} onChange={set('packCount')} />
          </label>
          <label className={form.field}>
            <span className={`${form.label} ${styles.labelWithInfo}`}>
              Qty increment step
              <span className={styles.infoWrap}>
                <span className={styles.infoBtn} aria-label="About qty increment step" tabIndex={0}>
                  <Info size={14} />
                </span>
                <span className={styles.infoTooltip} role="tooltip">
                  {form_.soldBy === 'WEIGHT'
                    ? 'Default 0.001 = 1 g, so POS can enter 130 g or any other gram amount. Quick chips must be multiples of this.'
                    : 'How much quantity goes up/down per click when ordering (default 1). Example: sold only in pairs → step 2.'}
                </span>
              </span>
            </span>
            <input
              className={form.input}
              type="number"
              min={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              step={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              value={form_.qtyStep}
              onChange={set('qtyStep')}
            />
          </label>
        </div>

        <div className={form.row}>
          <div className={form.field}>
            <span className={form.label}>{form_.soldBy === 'WEIGHT' ? 'Kg on hand' : 'Stock quantity'}</span>
            <input
              className={form.input}
              type="number"
              min="0"
              step={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              value={form_.stock}
              onChange={set('stock')}
              disabled={isEdit}
            />
            <span className={form.hint}>
              {isEdit
                ? 'Quantity on hand is changed in Inventory.'
                : 'Opening stock for the first lot. Later changes are made in Inventory.'}
            </span>
            {isEdit && product?.id && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={styles.stockInventoryBtn}
                onClick={() => navigate(`/inventory?productId=${encodeURIComponent(product.id)}`)}
              >
                Update stock in Inventory
              </Button>
            )}
          </div>
          <label className={form.field}>
            <span className={form.label}>{form_.soldBy === 'WEIGHT' ? 'Low stock alert below (kg)' : 'Low stock alert below'}</span>
            <input
              className={form.input}
              type="number"
              min="0"
              step={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              value={form_.lowStockThreshold}
              onChange={set('lowStockThreshold')}
              disabled={isEdit}
            />
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>{form_.soldBy === 'WEIGHT' ? 'Min kg per order' : 'Min qty per order'}</span>
            <input
              className={form.input}
              type="number"
              min={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              step={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              value={form_.minOrderQty}
              onChange={set('minOrderQty')}
            />
          </label>
          <label className={form.field}>
            <span className={form.label}>{form_.soldBy === 'WEIGHT' ? 'Max kg per order' : 'Max qty per order'}</span>
            <input
              className={form.input}
              type="number"
              min={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              step={form_.soldBy === 'WEIGHT' ? '0.001' : '1'}
              value={form_.maxOrderQty}
              onChange={set('maxOrderQty')}
            />
          </label>
        </div>
        <p className={form.hint} style={{ marginTop: -8, marginBottom: 16 }}>
          {form_.soldBy === 'WEIGHT'
            ? 'Loose defaults: 1 g step and 1 g minimum, so POS can sell any gram amount (130 g, 250 g, 2 kg).'
            : 'Quantity rules cap how many units a single customer can order in one basket.'}
        </p>

        <p className={form.label} style={{ marginBottom: 8 }}>Package dimensions</p>
        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Length</span>
            <input className={form.input} type="number" min="0" value={form_.dimLength} onChange={set('dimLength')} />
          </label>
          <label className={form.field}>
            <span className={form.label}>Width</span>
            <input className={form.input} type="number" min="0" value={form_.dimWidth} onChange={set('dimWidth')} />
          </label>
        </div>
        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Height</span>
            <input className={form.input} type="number" min="0" value={form_.dimHeight} onChange={set('dimHeight')} />
          </label>
          <label className={form.field}>
            <span className={form.label}>Unit</span>
            <select className={form.select} value={form_.dimUnit} onChange={set('dimUnit')}>
              {DIMENSION_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
        </div>

        {form_.itemType === 'food' && (
          <>
            <p className={form.label} style={{ marginBottom: 8 }}>Nutrition facts (per serving)</p>
            <div className={form.row}>
              <label className={form.field}>
                <span className={form.label}>Serving size</span>
                <input className={form.input} value={form_.nutritionServingSize} onChange={set('nutritionServingSize')} placeholder="100g" />
              </label>
              <label className={form.field}>
                <span className={form.label}>Energy (kcal)</span>
                <input className={form.input} type="number" min="0" value={form_.nutritionEnergyKcal} onChange={set('nutritionEnergyKcal')} />
              </label>
            </div>
            <div className={form.row}>
              <label className={form.field}>
                <span className={form.label}>Protein (g)</span>
                <input className={form.input} type="number" min="0" step="0.1" value={form_.nutritionProteinG} onChange={set('nutritionProteinG')} />
              </label>
              <label className={form.field}>
                <span className={form.label}>Carbs (g)</span>
                <input className={form.input} type="number" min="0" step="0.1" value={form_.nutritionCarbsG} onChange={set('nutritionCarbsG')} />
              </label>
            </div>
            <label className={form.field}>
              <span className={form.label}>Fat (g)</span>
              <input className={form.input} type="number" min="0" step="0.1" value={form_.nutritionFatG} onChange={set('nutritionFatG')} />
            </label>
          </>
        )}
      </form>
    </Drawer>
  );
}
